import axios, { AxiosError, AxiosInstance, AxiosResponse, CreateAxiosDefaults } from 'axios';

import {
  HttpClient,
  HttpClientError,
  HttpDeleteParams,
  HttpGetParams,
  HttpPatchParams,
  HttpPostParams,
  HttpPutParams,
  HttpResponse,
} from './HttpClient';

type AxiosHttpClientOptions = CreateAxiosDefaults;

export class AxiosHttpClient implements HttpClient {
  protected instance: AxiosInstance;

  constructor(options?: AxiosHttpClientOptions) {
    this.instance = axios.create(options);
  }

  private handleError(error: unknown): never {
    if (error instanceof AxiosError) {
      throw new HttpClientError(
        error.message,
        error?.response?.status || error.status,
        error.response?.data,
      );
    }

    throw error;
  }

  async delete<T>({
    headers,
    query,
    url,
    withCredentials,
  }: HttpDeleteParams): Promise<HttpResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.instance.delete(url, {
        headers,
        params: query,
        ...(withCredentials !== undefined ? { withCredentials } : {}),
      });

      return {
        body: response.data,
        statusCode: response.status,
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  async get<T>({
    headers,
    query,
    timeout,
    url,
    withCredentials,
  }: HttpGetParams): Promise<HttpResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.instance.get(url, {
        headers,
        params: query,
        timeout,
        ...(withCredentials !== undefined ? { withCredentials } : {}),
      });

      return {
        body: response.data,
        statusCode: response.status,
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  async patch<T>({
    body,
    headers,
    query,
    url,
    withCredentials,
  }: HttpPatchParams): Promise<HttpResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.instance.patch(url, body, {
        headers,
        params: query,
        ...(withCredentials !== undefined ? { withCredentials } : {}),
      });

      return {
        body: response.data,
        statusCode: response.status,
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  async post<T>({
    body,
    headers,
    query,
    url,
    withCredentials,
  }: HttpPostParams): Promise<HttpResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.instance.post(url, body, {
        headers,
        params: query,
        ...(withCredentials !== undefined ? { withCredentials } : {}),
      });

      return {
        body: response.data,
        statusCode: response.status,
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  async put<T>({
    body,
    headers,
    query,
    url,
    withCredentials,
  }: HttpPutParams): Promise<HttpResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.instance.put(url, body, {
        headers,
        params: query,
        ...(withCredentials !== undefined ? { withCredentials } : {}),
      });

      return {
        body: response.data,
        statusCode: response.status,
      };
    } catch (error) {
      this.handleError(error);
    }
  }
}
